<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import InboxesAPI from 'dashboard/api/inboxes';
import Icon from 'next/icon/Icon.vue';
import OnboardingStepLayout from './OnboardingStepLayout.vue';
import { useRegisterOnboardingNav } from '../useOnboardingNav';

const props = defineProps({
  // getting_started: inbox/labels/spaces; starting_with_ai: assistant (+ new widget inbox)
  variant: {
    type: String,
    default: 'getting_started',
    validator: value =>
      ['getting_started', 'starting_with_ai'].includes(value),
  },
});

const emit = defineEmits(['complete']);

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const STEP_DELAY_MS = 1100;

const inbox = computed(() => store.state.onboarding.inbox);
const bookingWidget = computed(() => store.state.onboarding.bookingWidget);
const channelType = computed(() => store.state.onboarding.channelType);
const isBookingChannel = computed(
  () =>
    props.variant === 'getting_started' &&
    channelType.value === 'booking_widget'
);
const assistantName = computed(
  () =>
    store.state.onboarding.assistantName ||
    store.getters['onboarding/suggestedAssistantName']
);
const assistantId = computed(() => store.state.onboarding.assistantId);
const createdSpaces = computed(
  () => store.state.onboarding.createdSpaces || []
);
const knowledgebaseIds = computed(
  () => store.state.onboarding.knowledgebaseIds || []
);

function hrefFor(route) {
  if (!route) return null;
  return router.resolve(route).href;
}

const checklist = computed(() => {
  if (props.variant === 'starting_with_ai') {
    const items = [];

    // Only show inbox when this AI phase just created a web widget.
    if (channelType.value === 'web_widget' && inbox.value?.id) {
      items.push({
        key: 'inbox',
        label: t('ONBOARDING_WIZARD.SETUP.INBOX'),
        ready: Boolean(inbox.value?.id),
        href: hrefFor(
          accountScopedRoute('settings_inbox_show', {
            inboxId: inbox.value.id,
          })
        ),
        linkLabel: t('ONBOARDING_WIZARD.SETUP.OPEN_INBOX'),
      });
    }

    items.push({
      key: 'assistant',
      label: t('ONBOARDING_WIZARD.SETUP.ASSISTANT', {
        name: assistantName.value || 'Roo',
      }),
      ready: Boolean(assistantId.value),
      href: assistantId.value
        ? hrefFor(
            accountScopedRoute('settings_assistant_show', {
              assistantId: assistantId.value,
            })
          )
        : null,
      linkLabel: t('ONBOARDING_WIZARD.SETUP.OPEN_ASSISTANT'),
    });

    return items;
  }

  const items = [];

  if (isBookingChannel.value) {
    items.push({
      key: 'booking_widget',
      label: t('ONBOARDING_WIZARD.SETUP.BOOKING_WIDGET'),
      ready: Boolean(bookingWidget.value?.id),
      href: bookingWidget.value?.id
        ? hrefFor(
            accountScopedRoute('settings_booking_widgets_show', {
              bookingWidgetId: bookingWidget.value.id,
            })
          )
        : null,
      linkLabel: t('ONBOARDING_WIZARD.SETUP.OPEN_BOOKING_WIDGET'),
    });
  } else {
    items.push({
      key: 'inbox',
      label: t('ONBOARDING_WIZARD.SETUP.INBOX'),
      ready: Boolean(inbox.value?.id),
      href: inbox.value?.id
        ? hrefFor(
            accountScopedRoute('settings_inbox_show', {
              inboxId: inbox.value.id,
            })
          )
        : null,
      linkLabel: t('ONBOARDING_WIZARD.SETUP.OPEN_INBOX'),
    });

    if (channelType.value === 'email' || channelType.value === 'microsoft') {
      items.push({
        key: 'labels',
        label: t('ONBOARDING_WIZARD.SETUP.LABELS'),
        ready: Boolean(inbox.value?.id),
        href: hrefFor(accountScopedRoute('labels_list')),
        linkLabel: t('ONBOARDING_WIZARD.SETUP.OPEN_LABELS'),
      });
    }
  }

  items.push({
    key: 'spaces',
    label:
      createdSpaces.value.length > 0
        ? t('ONBOARDING_WIZARD.SETUP.SPACES', {
            count: createdSpaces.value.length,
          })
        : t('ONBOARDING_WIZARD.SETUP.SPACES_SKIPPED'),
    ready: true,
    href:
      createdSpaces.value.length === 1
        ? hrefFor(
            accountScopedRoute('settings_spaces_show', {
              spaceId: createdSpaces.value[0].id,
            })
          )
        : hrefFor(accountScopedRoute('settings_spaces_index')),
    linkLabel: t('ONBOARDING_WIZARD.SETUP.OPEN_SPACES'),
    skipLink: createdSpaces.value.length === 0,
  });

  if (createdSpaces.value.length > 0) {
    const kbIds = knowledgebaseIds.value.length
      ? knowledgebaseIds.value
      : createdSpaces.value
          .map(space => space.knowledgebase_id)
          .filter(Boolean);

    items.push({
      key: 'knowledgebases',
      label:
        kbIds.length > 0
          ? t('ONBOARDING_WIZARD.SETUP.KNOWLEDGEBASES', {
              count: kbIds.length,
            })
          : t('ONBOARDING_WIZARD.SETUP.KNOWLEDGEBASES_INDEXING'),
      ready: true,
      href:
        kbIds.length === 1
          ? hrefFor(
              accountScopedRoute('settings_knowledgebases_content', {
                knowledgebaseId: kbIds[0],
              })
            )
          : hrefFor(accountScopedRoute('settings_knowledgebases_index')),
      linkLabel: t('ONBOARDING_WIZARD.SETUP.OPEN_KNOWLEDGEBASES'),
    });
  }

  return items;
});

const statuses = ref({});
const animationComplete = ref(false);

const allDone = computed(
  () =>
    animationComplete.value &&
    checklist.value.every(item => statuses.value[item.key] === 'done')
);

function sleep(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

function isItemReady(key) {
  return checklist.value.find(item => item.key === key)?.ready !== false;
}

async function resolveAssistantIdFromInbox() {
  if (store.state.onboarding.assistantId) return;
  const inboxId = store.state.onboarding.inbox?.id;
  if (!inboxId) return;
  try {
    const { data } = await InboxesAPI.getAgentBot(inboxId);
    const id = data?.id || data?.agent_bot?.id;
    if (id) store.commit('onboarding/SET_ASSISTANT_ID', id);
  } catch {
    // Inbox may not have an agent bot yet.
  }
}

async function waitUntilReady(key) {
  if (isItemReady(key)) return;
  await store.dispatch('onboarding/fetchStatus', { syncStep: false });
  if (key === 'assistant') await resolveAssistantIdFromInbox();

  const deadline = Date.now() + 20000;
  while (!isItemReady(key) && Date.now() < deadline) {
    await sleep(500);
    await store.dispatch('onboarding/fetchStatus', { syncStep: false });
    if (key === 'assistant') await resolveAssistantIdFromInbox();
  }

}

useRegisterOnboardingNav(() => ({
  hideBack: true,
  primary: {
    label: t('ONBOARDING_WIZARD.SETUP.CTA'),
    disabled: !allDone.value,
    onClick: () => emit('complete'),
  },
}));

onMounted(async () => {
  const needsStatus = isBookingChannel.value
    ? !bookingWidget.value?.id
    : !inbox.value?.id || !assistantId.value;
  if (needsStatus) {
    await store.dispatch('onboarding/fetchStatus', { syncStep: false });
  }
  if (props.variant === 'starting_with_ai' && !assistantId.value) {
    await resolveAssistantIdFromInbox();
  }

  const initial = {};
  checklist.value.forEach(item => {
    initial[item.key] = 'pending';
  });
  statuses.value = initial;

  for (const item of checklist.value) {
    statuses.value = { ...statuses.value, [item.key]: 'loading' };
    await sleep(STEP_DELAY_MS);
    await waitUntilReady(item.key);
    statuses.value = { ...statuses.value, [item.key]: 'done' };
  }

  animationComplete.value = true;
});

watch(
  () => [inbox.value?.id, assistantId.value, bookingWidget.value?.id],
  () => {
    if (isBookingChannel.value) {
      if (!bookingWidget.value?.id) {
        store.dispatch('onboarding/fetchStatus', { syncStep: false });
      }
      return;
    }
    if (!inbox.value?.id) {
      store.dispatch('onboarding/fetchStatus', { syncStep: false });
    }
  }
);
</script>

<template>
  <OnboardingStepLayout
    :step-label="
      variant === 'starting_with_ai'
        ? t('ONBOARDING_WIZARD.OVERVIEW.PHASES.starting_with_ai.TITLE')
        : t('ONBOARDING_WIZARD.PHASES.3.LABEL')
    "
    :title="
      allDone
        ? t('ONBOARDING_WIZARD.SETUP.TITLE_DONE')
        : t('ONBOARDING_WIZARD.SETUP.TITLE')
    "
    :description="
      allDone
        ? t('ONBOARDING_WIZARD.SETUP.DESCRIPTION_DONE')
        : t('ONBOARDING_WIZARD.SETUP.DESCRIPTION')
    "
  >
    <ul class="w-full flex flex-col gap-3 m-0 p-0 list-none">
      <li
        v-for="item in checklist"
        :key="item.key"
        class="flex items-center gap-4 rounded-xl border bg-white dark:bg-slate-900/40 transition-all duration-500 ease-out overflow-hidden"
        :class="
          statuses[item.key] === 'pending'
            ? 'opacity-0 translate-x-8 max-h-0 py-0 px-4 border-transparent pointer-events-none'
            : 'opacity-100 translate-x-0 max-h-24 px-4 py-3 border-slate-100 dark:border-slate-800'
        "
      >
        <div
          class="size-9 shrink-0 rounded-full flex items-center justify-center"
          :class="{
            'bg-woot-50 dark:bg-woot-900/30': statuses[item.key] === 'loading',
            'bg-green-50 dark:bg-green-900/20': statuses[item.key] === 'done',
          }"
        >
          <span
            v-if="statuses[item.key] === 'loading'"
            class="size-4 border-2 border-woot-500 border-t-transparent rounded-full animate-spin"
          />
          <Icon
            v-else-if="statuses[item.key] === 'done'"
            icon="i-lucide-check"
            class="size-5 text-green-600 dark:text-green-400"
          />
        </div>

        <div class="flex-1 min-w-0">
          <p class="m-0 text-base font-medium text-slate-900 dark:text-white">
            {{ item.label }}
          </p>
          <a
            v-if="statuses[item.key] === 'done' && item.href && !item.skipLink"
            :href="item.href"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 mt-0.5 text-sm text-woot-600 dark:text-woot-400 hover:underline"
          >
            {{ item.linkLabel }}
            <Icon icon="i-lucide-external-link" class="size-3.5" />
          </a>
        </div>
      </li>
    </ul>
  </OnboardingStepLayout>
</template>
